/**
 * plugin 市集（批153）的畫面規則：狀態標籤、dl 同步狀態、權限判斷、公鑰指紋。
 *
 * ★ 權限一律**具名比較** `ROLE_RANK`（不准寫死數字——批110 插入 manager 時 superadmin 從 3 變 4，
 *   寫死 `>= 3` 的地方全錯）。這裡只決定**按鈕給不給看**，真正的閘在後端。
 */
import type { GrantCode, MarketPlugin, MarketVersion, PluginDetail, PluginGrant, ReviewQueue } from '@/api/pluginMarket'
import { ApiError } from '@/api/client'
import { ROLE_RANK, rankOf } from '@/stores/auth'

export const VISIBILITY_LABELS: Record<string, string> = { private: '私人', public: '公開' }
export const PLUGIN_STATUS_LABELS: Record<string, string> = { active: '啟用', suspended: '已停權' }
export const VERSION_STATUS_LABELS: Record<string, string> = {
  uploading: '上傳中',
  ready: '已上架',
  failed: '上傳失敗',
  withdrawn: '已撤回',
}
/** 版本狀態 → 全域 `.status-pill` 的修飾 class。 */
export const VERSION_STATUS_PILL: Record<string, string> = {
  uploading: 'pending',
  ready: 'active',
  failed: 'revoked',
  withdrawn: 'superseded',
}
export const GRANT_SOURCE_LABELS: Record<string, string> = {
  staff: '我方指派',
  grant_code: '指派碼',
  publisher_org: '發行者組織',
}

export function labelOf(map: Record<string, string>, key: string | null | undefined): string {
  if (!key) return '—'
  return map[key] ?? key
}

/** 權利的「現在有沒有效」：active＋未到期＝生效；到期不改 status，看 `expired`。 */
export type GrantState = 'effective' | 'expired' | 'revoked'
export function grantState(g: Pick<PluginGrant, 'status' | 'expired'>): GrantState {
  if (g.status !== 'active') return 'revoked'
  return g.expired ? 'expired' : 'effective'
}
export const GRANT_STATE_LABELS: Record<GrantState, string> = {
  effective: '生效中',
  expired: '已到期',
  revoked: '已撤銷',
}
export const GRANT_STATE_PILL: Record<GrantState, string> = {
  effective: 'active',
  expired: 'expired',
  revoked: 'revoked',
}

/**
 * dl 同步狀態：
 * - `none`：dl 上還沒有這條 channel（從沒上傳過）。
 * - `error`：最近一次同步失敗（`dl_error` 有值）。
 * - `pending`：dl 的存取與期望值不一致（上架／下架的 dl 那一半還沒完成，背景 60 秒內重試）。
 * - `synced`：一致。
 */
export type DlSyncState = 'none' | 'synced' | 'pending' | 'error'
export function dlSyncState(
  p: Pick<MarketPlugin, 'dl_access' | 'dl_error'> & Partial<Pick<PluginDetail, 'desired_dl_access'>>,
): DlSyncState {
  if (p.dl_error) return 'error'
  if (!p.dl_access) return 'none'
  if (p.desired_dl_access && p.desired_dl_access !== p.dl_access) return 'pending'
  return 'synced'
}
export const DL_SYNC_LABELS: Record<DlSyncState, string> = {
  none: '尚未建立',
  synced: '已同步',
  pending: '同步中',
  error: '同步失敗',
}

/** 改 plugin 狀態、發行者、簽章公鑰、上架與下架：營運主管以上。 */
export function canManageMarket(role: string | null | undefined): boolean {
  return rankOf(role) >= ROLE_RANK.manager
}
/** 上架／下架按鈕給不給看（＝`canManageMarket`，另立名字是讓模板讀得懂在問什麼）。 */
export function canPublish(role: string | null | undefined): boolean {
  return canManageMarket(role)
}
/** 登記、上傳、撤回、權利：admin 以上。 */
export function canOperateMarket(role: string | null | undefined): boolean {
  return rankOf(role) >= ROLE_RANK.admin
}

/** 公開前的打字確認：要逐字輸入 plugin key（前後空白不算）。 */
export function publishConfirmOk(typed: string, key: string): boolean {
  return typed.trim() === key
}

/** 判斷版本狀態用的最小形狀（`review_status` 缺席＝不必審的舊投影）。 */
type VersionLite = Pick<MarketVersion, 'status'> & { review_status?: string | null }

/**
 * 「已發布」的版本＝`ready` 而且**不在審核中**（R3-9：與後端 `plugin_registry.version_released()` 同一句話）。
 * 審核中的版本 `status` 也是 `ready`，但它不在 feed、客戶看不到——拿它當「有已上架版本」，
 * 按〈公開〉會拿到 409 `no_ready_version`，下載包也會抓到還沒審過的那一版。
 */
export function isReleasedVersion(v: VersionLite): boolean {
  return v.status === 'ready' && v.review_status !== 'pending'
}

/** 有沒有至少一個已發布的版本——上架的前提（審核中的不算）。 */
export function hasReadyVersion(versions: VersionLite[]): boolean {
  return versions.some(isReleasedVersion)
}

/** 最新一個已發布的版本（版本清單是新到舊；審核中的跳過）。沒有回 null。 */
export function latestReleasedVersion<T extends VersionLite>(versions: T[]): T | null {
  return versions.find(isReleasedVersion) ?? null
}

/**
 * 版本可不可以撤回（已上架或上傳失敗的才行）。
 * - **已婉拒的不行**（R3-10）：婉拒的版本 `status` 是 `failed`，撤回會把那個版號永久佔掉——
 *   發行者本來可以修正後用同一個版號重傳。
 * - **核准處理中的不行**（R1-3）：已按過核准、發布主機那一半還沒完成（`review_status='pending'`＋
 *   `reviewed_at` 有值）——後端一律回 409 `review_in_progress`，要等核准完成才能撤回。
 */
export function canWithdraw(v: VersionLite & { reviewed_at?: string | null; approving?: boolean }): boolean {
  if (v.review_status === 'rejected') return false
  if (isApproving(v)) return false
  return v.status === 'ready' || v.status === 'failed'
}

/** 撤回按鈕反灰時的說明（核准處理中）。 */
export const WITHDRAW_BLOCKED_APPROVING = '核准處理中，完成後才能撤回'

/**
 * 市集排序欄（R3-18）：先 trim，空字串**不可以當成 0**（`Number('')` 是 0，會排到最前面）。
 * 回錯誤訊息；null＝可以送。
 */
export function sortOrderError(raw: string): string | null {
  const s = raw.trim()
  if (!s) return '請輸入排序'
  if (!/^\d+$/.test(s)) return '排序要是 0–100000 的整數'
  const n = Number(s)
  if (n > 100000) return '排序要是 0–100000 的整數'
  return null
}

/**
 * 上傳成功之後補寫版本說明（R3-1）：失敗就問要不要重試（`askRetry` 回 true＝再試一次）。
 * 成功回 `save()` 的結果；使用者放棄回 null。
 */
export async function saveNotesWithRetry<T>(
  save: () => Promise<T>,
  askRetry: (err: unknown) => Promise<boolean>,
): Promise<T | null> {
  for (;;) {
    try {
      return await save()
    } catch (e) {
      if (!(await askRetry(e))) return null
    }
  }
}

const B64_RE = /^[A-Za-z0-9+/]{43}=$/

/**
 * 公鑰（raw 32 bytes 的標準 base64）→ raw bytes；格式不對回 null。前端只擋形狀，權威在後端。
 */
export function decodePublicKey(raw: string): Uint8Array<ArrayBuffer> | null {
  const s = raw.trim()
  if (!B64_RE.test(s)) return null
  try {
    const bin = atob(s)
    if (bin.length !== 32) return null
    const out = new Uint8Array(32)
    for (let i = 0; i < 32; i++) out[i] = bin.charCodeAt(i)
    return out
  } catch {
    return null
  }
}

/** 公鑰指紋＝sha256(raw 32 bytes) 小寫 hex（與後端、dl、CS 同一個定義）。格式不對回 null。 */
export async function publicKeyFingerprint(raw: string): Promise<string | null> {
  const bytes = decodePublicKey(raw)
  if (!bytes) return null
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/** 指紋分段顯示（每 8 碼一段），方便人眼帶外核對。 */
export function formatFingerprint(fp: string | null | undefined): string {
  if (!fp) return '—'
  return fp.match(/.{1,8}/g)?.join(' ') ?? fp
}

export function formatBytes(n: number | null | undefined): string {
  if (n == null) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

/** plugin key 的前端形狀檢查（與後端 `^[a-z][a-z0-9_]{0,31}$` 相同）。 */
export const PLUGIN_KEY_RE = /^[a-z][a-z0-9_]{0,31}$/

/** `<input type="date">` 的值（YYYY-MM-DD）→ 當天結束（本地時間 23:59:59）的 ISO 字串；空＝null。 */
export function dateToExpiry(date: string): string | null {
  const s = date.trim()
  if (!s) return null
  const d = new Date(`${s}T23:59:59`)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

/** ISO → `<input type="date">` 的值（本地日期）。 */
export function expiryToDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

// ===========================================================================
// 批B：上架申請、版本審核、發行者開通、指派碼
// ===========================================================================

export const LISTING_STATUS_LABELS: Record<string, string> = {
  none: '未申請',
  pending: '申請中',
  approved: '已核准',
  rejected: '已婉拒',
}
export const LISTING_STATUS_PILL: Record<string, string> = {
  none: 'superseded',
  pending: 'pending',
  approved: 'active',
  rejected: 'revoked',
}
export const REVIEW_STATUS_LABELS: Record<string, string> = {
  pending: '待審核',
  approved: '已核准',
  rejected: '已婉拒',
}

/**
 * 版本在畫面上的狀態（批B）。**審核狀態優先於 `status`**：
 * - 待審的版本 `status` 是 `ready`，但它不在 feed、客戶看不到——印「已上架」是說謊，印「待審核」。
 * - 婉拒的版本 `status` 是 `failed`，印「上傳失敗」會讓發行者以為要重傳同一個檔，印「已婉拒」。
 * - 撤回永遠是撤回（不管審過沒有）。
 */
export function versionDisplay(
  v: VersionLite & { reviewed_at?: string | null; approving?: boolean },
): { label: string; pill: string } {
  if (v.status !== 'withdrawn') {
    if (isApproving(v)) return { label: '核准處理中', pill: 'pending' }
    if (v.review_status === 'pending' && v.status === 'ready') return { label: '待審核', pill: 'pending' }
    if (v.review_status === 'rejected') return { label: '已婉拒', pill: 'revoked' }
  }
  return { label: labelOf(VERSION_STATUS_LABELS, v.status), pill: VERSION_STATUS_PILL[v.status] ?? '' }
}

/**
 * 「核准處理中」：已按過核准、正在等發布主機那一半（`review_status` 仍是 pending、`reviewed_at` 已有值）。
 * 審核佇列直接帶 `approving`；詳情頁的版本投影沒有這一欄，由 `reviewed_at` 推。這種版本不能再核准或婉拒。
 */
export function isApproving(
  v: VersionLite & { reviewed_at?: string | null; approving?: boolean },
): boolean {
  if (v.approving !== undefined) return v.approving
  return v.review_status === 'pending' && v.status === 'ready' && !!v.reviewed_at
}

/** 這一版是不是待審、而且還能核准或婉拒（核准處理中的不算）。 */
export function isPendingReview(
  v: VersionLite & { reviewed_at?: string | null; approving?: boolean },
): boolean {
  return v.review_status === 'pending' && v.status === 'ready' && !isApproving(v)
}

/** 第三方 plugin（第一方不審、不走 portal；`first_party` 缺席時保守當成第一方＝不顯示審核相關 UI）。 */
export function isThirdParty(p: Pick<MarketPlugin, 'first_party'>): boolean {
  return p.first_party === false
}

/**
 * 上架按鈕能不能按（批B）：第一方照舊（有 ready 版本＋啟用中）；**第三方要發行者先在 portal
 * 申請**（`listing_status='pending'`），這時 publish 就是「核准上架申請」。
 */
export function canApproveListing(
  p: Pick<MarketPlugin, 'first_party' | 'listing_status' | 'status' | 'visibility'>,
  versions: VersionLite[],
): boolean {
  if (p.visibility === 'public' || p.status !== 'active' || !hasReadyVersion(versions)) return false
  return isThirdParty(p) ? p.listing_status === 'pending' : true
}

/**
 * 申請上架期間凍結上傳（批153 補充契約 C3）：第三方 plugin 的 `listing_status='pending'` 時，後端 `begin_upload`
 * 一律 409 `listing_review_in_progress`——上傳區停用、在固定位置顯示這句。第一方不受影響。
 */
export const LISTING_REVIEW_FREEZE_TEXT = '上架申請審核中，取消申請後才能上傳新版。'
export function isUploadFrozen(p: Pick<MarketPlugin, 'first_party' | 'listing_status'> | null | undefined): boolean {
  return !!p && isThirdParty(p) && p.listing_status === 'pending'
}

/**
 * 核准後發布主機那一半沒完成時的說明（R3-8）。背景對帳的重試間隔約 10 分鐘——不要寫「每分鐘」；
 * 〈重試核准〉會原地重跑那一半（後端 `plugin_versions.approve`：不重記稽核、審核者不變）。
 */
export const APPROVAL_PENDING_TEXT =
  '核准已記下；把包搬到市集的那一步這次沒有完成。背景約 10 分鐘內會自動接手，也可以按〈重試核准〉手動重試。' +
  '完成之前這一版會顯示「核准處理中」。'

/** 審核佇列的總筆數（側欄徽章）。 */
export function reviewQueueCount(q: Pick<ReviewQueue, 'listing_pending' | 'version_pending'> | null | undefined): number {
  if (!q) return 0
  return (q.listing_pending?.length ?? 0) + (q.version_pending?.length ?? 0)
}

/** 下載的檔名：伺服器沒給就用 `<key>-<version>.ipoplug`。 */
export function packageFilename(key: string, version: string, fromServer?: string | null): string {
  return fromServer || `${key}-${version}.ipoplug`
}

// --- 發行者開通 -------------------------------------------------------------------------------

/** key 前綴（與後端相同）：小寫英文開頭、3–14 個小寫英數字、最後一個底線。 */
export const PUBLISHER_PREFIX_RE = /^[a-z][a-z0-9]{2,13}_$/

/** 前綴的即時檢查（空字串回 null＝還沒開始打，不顯示錯誤）。 */
export function prefixError(raw: string): string | null {
  const s = raw.trim()
  if (!s) return null
  if (PUBLISHER_PREFIX_RE.test(s)) return null
  if (/[A-Z]/.test(s)) return '前綴只收小寫英文與數字'
  if (!s.endsWith('_')) return '前綴最後要加一個底線，例如「acme_」'
  if (!/^[a-z]/.test(s)) return '前綴要以小寫英文字母開頭'
  const body = s.slice(0, -1)
  if (!/^[a-z0-9]*$/.test(body)) return '底線之前只能是小寫英文與數字（中間不可再有底線）'
  return '底線之前要有 3–14 個字元'
}

/** 顯示名稱 1–120 字（trim 後）。 */
export function displayNameError(raw: string): string | null {
  const s = raw.trim()
  if (!s) return '顯示名稱不可空白'
  if (s.length > 120) return '顯示名稱最多 120 字'
  return null
}

/*
 * 後端錯誤碼 → 中文。**碼與句子一律照 `license-server/docs/PLUGIN_MARKET.md` §15.5 的總表**
 * （後端每個錯誤都帶可直接顯示的 `message`，這裡的表是同一句的前端備份、也讓測試釘得住碼名）。
 * 句子裡有動態內容的碼（`invalid_field`、`key_prefix_required`、`key_mismatch`…）**不列**——
 * 一律顯示後端給的 `message`。
 */

/** 開通發行者（#2）。 */
export const PUBLISHER_ERROR_TEXT: Record<string, string> = {
  invalid_key_prefix: '前綴必須符合 ^[a-z][a-z0-9]{2,13}_$（例：acme_）。',
  customer_not_found: '找不到這個客戶。',
  already_publisher: '這個組織已經是發行者。',
  key_prefix_unavailable: '這個前綴無法分配，請換一個。',
}

/** 發碼與作廢（#21～#23）。 */
export const GRANT_CODE_ERROR_TEXT: Record<string, string> = {
  expires_in_past: '指派碼的有效期限必須晚於現在。',
  expires_too_far: '指派碼的有效期限最長 365 天。',
  plugin_is_public: '公開的擴充任何站台都能安裝，不需要指派碼。',
  plugin_suspended: '停權中的擴充（或發行者）不能發指派碼。',
  code_not_voidable: '只有尚未兌換的指派碼可以作廢。',
}

/** 上傳（#11；補充契約 C3）。 */
export const UPLOAD_ERROR_TEXT: Record<string, string> = {
  listing_review_in_progress: LISTING_REVIEW_FREEZE_TEXT,
}

/** 審核（#14 兼核准、#16、#27～#29）。 */
export const REVIEW_ERROR_TEXT: Record<string, string> = {
  listing_not_requested: '第三方擴充要先由發行者在 portal 申請公開，才能核准上架。',
  note_required: '請填寫理由（發行者會看到）。',
  version_not_pending: '只有審核中的版本可以核准或婉拒。',
  review_in_progress: '這一版已經在核准中（等發布主機那一半完成），不能婉拒。',
  version_not_downloadable: '只有已上架（含審核中）的版本可以下載。',
  market_unavailable: '擴充市集目前未啟用（未設定命名空間或市集金鑰不在）。',
}

/** 422 型別錯誤（FastAPI 預設，`detail` 是陣列）與 IP 限速 429（`detail` 是英文字串）的通用句。 */
export const GENERIC_422_TEXT = '送出的資料格式不正確，請重新整理頁面後再試。'
export const GENERIC_429_TEXT = '操作太頻繁了，請稍後再試。'

/** `ApiError` 的 `detail.code`（沒有就 null）。 */
export function apiErrorCode(e: unknown): string | null {
  if (!(e instanceof ApiError) || !e.payload) return null
  const d = (e.payload as { detail?: unknown }).detail
  if (d && typeof d === 'object' && typeof (d as { code?: unknown }).code === 'string') {
    return (d as { code: string }).code
  }
  return null
}

/**
 * 錯誤訊息：已知碼用本地中文 → 後端 `message` → 429／422 的通用中文 → 字串 detail（只收含中文的）→ 預設句。
 *
 * ★ 429 的 `detail` 是英文字串 `too many requests`、422 型別錯誤的 `detail` 是 FastAPI 的陣列——
 *   兩種都不能原樣丟給使用者看。
 */
export function apiErrorText(e: unknown, known: Record<string, string>, fallback: string): string {
  const code = apiErrorCode(e)
  if (code && known[code]) return known[code]
  if (!(e instanceof ApiError)) return fallback
  const d = e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  if (d && typeof d === 'object' && !Array.isArray(d) && typeof (d as { message?: unknown }).message === 'string') {
    return (d as { message: string }).message
  }
  if (e.status === 429) return GENERIC_429_TEXT
  if (e.status === 422 && (Array.isArray(d) || d === undefined)) return GENERIC_422_TEXT
  if (typeof d === 'string' && /[㐀-鿿]/.test(d)) return d
  return fallback
}

// --- 指派碼 -------------------------------------------------------------------------------------

export type GrantCodeState = 'issued' | 'redeemed' | 'void' | 'expired'
const GRANT_CODE_STATES: readonly string[] = ['issued', 'redeemed', 'void', 'expired']
/**
 * 碼的狀態：伺服器給了 `state` 就用它；沒有就自己算（過期不改 `status`：`issued` 但 `expires_at` 已過＝過期）。
 */
export function grantCodeState(
  c: Pick<GrantCode, 'status' | 'expires_at'> & { state?: string },
  now: number = Date.now(),
): GrantCodeState {
  if (c.state && GRANT_CODE_STATES.includes(c.state)) return c.state as GrantCodeState
  if (c.status === 'redeemed') return 'redeemed'
  if (c.status === 'void') return 'void'
  const t = Date.parse(c.expires_at)
  if (!Number.isNaN(t) && t <= now) return 'expired'
  return 'issued'
}
export const GRANT_CODE_STATE_LABELS: Record<GrantCodeState, string> = {
  issued: '未使用',
  redeemed: '已兌換',
  void: '已作廢',
  expired: '已過期',
}
export const GRANT_CODE_STATE_PILL: Record<GrantCodeState, string> = {
  issued: 'active',
  redeemed: 'superseded',
  void: 'revoked',
  expired: 'expired',
}
/**
 * 碼在畫面上的狀態（批B 走查修正）。plugin **公開中**時，所有碼一律兌換不了（LS 回 `grant_code_invalid`）
 * ——還沒用過的碼印「未使用」會讓人以為還能發出去用，改印「公開中無法使用」、中性灰。
 * 〈作廢〉照樣可以按（`canVoidGrantCode` 不看可見性），方便清理。
 */
export type GrantCodeDisplayState = GrantCodeState | 'public_blocked'
export function grantCodeDisplay(
  c: Pick<GrantCode, 'status' | 'expires_at'> & { state?: string },
  pluginVisibility: string | null | undefined,
  now: number = Date.now(),
): { state: GrantCodeDisplayState; label: string; pill: string } {
  const s = grantCodeState(c, now)
  if (s === 'issued' && pluginVisibility === 'public') {
    return { state: 'public_blocked', label: '公開中無法使用', pill: 'superseded' }
  }
  return { state: s, label: GRANT_CODE_STATE_LABELS[s], pill: GRANT_CODE_STATE_PILL[s] }
}

/** 只有「未使用」的碼可以作廢（已兌換的要撤銷的是權利，不是碼）。 */
export function canVoidGrantCode(c: Pick<GrantCode, 'status' | 'expires_at'>, now: number = Date.now()): boolean {
  return grantCodeState(c, now) === 'issued'
}
/** 清單只看得到末四碼：`PG-••••-••••-••••-ABCD`。 */
export function maskedGrantCode(hint: string | null | undefined): string {
  return hint ? `PG-••••-••••-••••-${hint}` : '—'
}

export const GRANT_CODE_MAX_COUNT = 50
export const GRANT_CODE_MAX_DAYS = 365
export const GRANT_CODE_NOTE_MAX = 200
/** 兌換後權利天數的上限（契約沒給上界；十年足夠，也擋住手滑多打幾個 0）。 */
export const GRANT_TERM_MAX_DAYS = 3650

const DAY = 86_400_000

/** 本地日期字串 `YYYY-MM-DD` → 那天 00:00 的 epoch（解析不出來回 NaN）。 */
function localMidnight(date: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim())
  if (!m) return Number.NaN
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime()
}
function todayMidnight(now: number): number {
  const d = new Date(now)
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

/** `<input type="date">` 的上下限（明天～今天＋365 天）。 */
export function grantCodeDateBounds(now: number = Date.now()): { min: string; max: string } {
  const t = todayMidnight(now)
  const fmt = (ms: number) => {
    const d = new Date(ms)
    const p = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
  }
  // 用「加天數後重建日期」而不是加毫秒：跨夏令時間的地區加 24h 會差一小時、落到前一天。
  const d = new Date(t)
  const min = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime()
  const max = new Date(d.getFullYear(), d.getMonth(), d.getDate() + GRANT_CODE_MAX_DAYS).getTime()
  return { min: fmt(min), max: fmt(max) }
}

/**
 * 到期日 → 送給後端的 ISO：那天結束（23:59:59 本地），但**不超過「現在＋365 天」**——
 * 選最後一天時，當天結束會比 365×24 小時多出幾個鐘頭，後端以時間戳比就會退件。
 */
export function grantCodeExpiryIso(date: string, now: number = Date.now()): string | null {
  const iso = dateToExpiry(date)
  if (!iso) return null
  const cap = now + GRANT_CODE_MAX_DAYS * DAY - 60_000
  const t = Date.parse(iso)
  return new Date(Math.min(t, cap)).toISOString()
}

export interface GrantCodeForm {
  /** `YYYY-MM-DD`（必填）。 */
  expires: string
  /** 兌換後權利天數（字串；空＝不到期）。 */
  termDays: string
  note: string
  /** 份數（字串）。 */
  count: string
}
export function emptyGrantCodeForm(): GrantCodeForm {
  return { expires: '', termDays: '', note: '', count: '1' }
}

/** 整數字串（全形數字不收；前後空白可）→ 數字；不是整數回 null。 */
function intOf(s: string): number | null {
  const t = s.trim()
  if (!/^\d+$/.test(t)) return null
  return Number(t)
}

/** 發碼表單的檢查（null＝可以送）。 */
export function grantCodeFormError(f: GrantCodeForm, now: number = Date.now()): string | null {
  if (!f.expires.trim()) return '請選擇碼的到期日'
  const day = localMidnight(f.expires)
  if (Number.isNaN(day)) return '到期日格式不對'
  const diff = Math.round((day - todayMidnight(now)) / DAY)
  if (diff < 1 || diff > GRANT_CODE_MAX_DAYS) return `碼的到期日要在明天到 ${GRANT_CODE_MAX_DAYS} 天後之間`
  if (f.termDays.trim()) {
    const n = intOf(f.termDays)
    if (n === null || n < 1 || n > GRANT_TERM_MAX_DAYS) return `權利天數要是 1–${GRANT_TERM_MAX_DAYS} 的整數（留空＝不到期）`
  }
  if (f.note.trim().length > GRANT_CODE_NOTE_MAX) return `備註最多 ${GRANT_CODE_NOTE_MAX} 字`
  const c = intOf(f.count)
  if (c === null || c < 1 || c > GRANT_CODE_MAX_COUNT) return `份數要是 1–${GRANT_CODE_MAX_COUNT} 的整數`
  return null
}

/** 表單 → 送出的 body（先過 `grantCodeFormError`）。 */
export function grantCodeBody(
  f: GrantCodeForm,
  now: number = Date.now(),
): { expires_at: string; grant_term_days: number | null; note: string | null; count: number } {
  return {
    expires_at: grantCodeExpiryIso(f.expires, now) ?? '',
    grant_term_days: f.termDays.trim() ? Number(f.termDays.trim()) : null,
    note: f.note.trim() || null,
    count: Number(f.count.trim()),
  }
}

/** 一次性明碼的「全部複製」內容：一行一組。 */
export function issuedCodesText(items: { code: string }[]): string {
  return items.map((i) => i.code).join('\n')
}

/** 權利天數的顯示（null＝不到期）。 */
export function grantTermLabel(days: number | null | undefined): string {
  return days ? `兌換後 ${days} 天` : '兌換後不到期'
}
